import React, { useEffect, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import {
    Grid,
    Paper,
    TableContainer,
    Table,
    CircularProgress,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
    Button,
    IconButton,
    Tooltip,
} from '@material-ui/core'
import EditOutlined from '@material-ui/icons/EditOutlined'
import AddIcon from '@material-ui/icons/Add'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/core'
import { fetchMarkets, MarketModel } from '../../redux/actions/dashboard'
import MarketDialog from './MarketDialog'
import { useSelector, useDispatch } from 'react-redux'

const marketStyles = makeStyles((theme: any) => ({
    headerWrapper: {
        padding: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    actionsCell: {
        width: 1,
        whiteSpace: 'nowrap',
    },
    paper: {
        width: '100%',
        margin: theme.spacing(1),
    },
    table: {
        overflowY: 'hidden',
        overflowX: 'hidden',
    },
    row: {
        padding: 0,
    },
    circularProgressWrapper: {
        padding: theme.spacing(6),
    },
    sectionMargin: {
        marginTop: theme.spacing(2),
    },
}))

interface Props {
    // len superadmin smie markety pridavat / upravovat
    editable?: boolean
    onSuccess?: (msg: string) => void
    onError?: (msg: string) => void
}

export default function Markets({ editable = false, onSuccess, onError }: Props) {
    const classes = marketStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const marketsState = useSelector((state: any) => state.markets)
    // undefined = zavrety dialog, { market: undefined } = novy market
    const [dialog, setDialog] = useState<{ market?: MarketModel }>()

    useEffect(() => {
        dispatch(fetchMarkets())
    }, [dispatch])

    return (
        <>
            <Grid container className={classes.sectionMargin}>
                <Grid className={classes.headerWrapper}>
                    <Typography align="left" variant="h6">
                        {t('dashboard.marketTitle')}
                    </Typography>
                    {editable ? (
                        <Button
                            size="small"
                            color="primary"
                            startIcon={<AddIcon fontSize="small" />}
                            onClick={() => setDialog({})}
                        >
                            {t('dashboard.addMarket')}
                        </Button>
                    ) : null}
                </Grid>
                <Grid item sm={12}>
                    <Paper className={classes.paper}>
                        <TableContainer>
                            <Table
                                stickyHeader
                                className={classes.table}
                                aria-labelledby="tableTitle"
                                size={'small'}
                                aria-label="enhanced table"
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('common.name')}</TableCell>
                                        <TableCell>{t('dashboard.marketUrl')}</TableCell>
                                        <TableCell>{t('common.description')}</TableCell>
                                        {editable ? <TableCell className={classes.actionsCell} /> : null}
                                    </TableRow>
                                </TableHead>

                                {!marketsState.value ? (
                                    <TableBody>
                                        <TableRow>
                                            <TableCell
                                                colSpan={7}
                                                align="center"
                                                className={classes.circularProgressWrapper}
                                            >
                                                <CircularProgress size={40} />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                ) : (
                                    <TableBody>
                                        {marketsState.value.map((market: any, index: number) => {
                                            let color = 'white'
                                            return (
                                                <TableRow key={index} style={{ backgroundColor: color }}>
                                                    <TableCell align="left">
                                                        {market.name ?? t('common.none')}
                                                    </TableCell>
                                                    <TableCell align="left">
                                                        {market.url ? (
                                                            <a
                                                                href={market.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {market.url}
                                                            </a>
                                                        ) : (
                                                            t('common.none')
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="left">
                                                        {market.description ?? t('common.none')}
                                                    </TableCell>
                                                    {editable ? (
                                                        <TableCell className={classes.actionsCell}>
                                                            <Tooltip title={t('dashboard.editMarket') as string}>
                                                                <IconButton size="small" onClick={() => setDialog({ market })}>
                                                                    <EditOutlined fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    ) : null}
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                )}
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
            {editable ? (
                <MarketDialog
                    open={Boolean(dialog)}
                    market={dialog?.market}
                    onClose={() => setDialog(undefined)}
                    onSaved={() => {
                        setDialog(undefined)
                        onSuccess?.(t('dashboard.marketSaved'))
                        dispatch(fetchMarkets())
                    }}
                    onError={(msg) => onError?.(msg)}
                />
            ) : null}
        </>
    )
}
